import {
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactElement,
  type Ref,
} from "react";

import { cn } from "@/ui/lib";
import {
  ITEM_CLASS,
  ITEM_DEFAULTS,
  ITEM_PADDING_CLASS_MAP,
  ITEM_SIZE_CLASS_MAP,
  ITEM_VARIANT_CLASS_MAP,
  TItemChildProps,
  TItemProps,
} from "@/ui/components/data-display/Item/Item.config";

export const Item = forwardRef<HTMLElement, TItemProps>(
  (
    {
      "data-component": component = ITEM_DEFAULTS["data-component"],
      variant = ITEM_DEFAULTS.variant,
      size = ITEM_DEFAULTS.size,
      asChild = ITEM_DEFAULTS.asChild,
      padding = ITEM_DEFAULTS.padding,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const itemClassName = cn(
      ITEM_CLASS,
      ITEM_VARIANT_CLASS_MAP[variant],
      ITEM_SIZE_CLASS_MAP[size],
      ITEM_PADDING_CLASS_MAP[padding],
      className,
    );

    const sharedProps = {
      "data-component": component,
      "data-variant": variant,
      "data-size": size,
    };

    if (asChild && isValidElement(children)) {
      const child = children as ReactElement<TItemChildProps>;

      return cloneElement(child, {
        ...props,
        ref,
        ...sharedProps,
        className: cn(itemClassName, child.props.className),
      });
    }

    return (
      <div
        {...props}
        ref={ref as Ref<HTMLDivElement>}
        {...sharedProps}
        className={itemClassName}
      >
        { children }
      </div>
    );
  },
);

Item.displayName = "Item";
